/**
 * CaregiverSummary — campus roll-up.
 *
 * Laid out as a hierarchy rather than a uniform grid of tiles. Six identical
 * boxes give every number the same weight, which makes the reader do the work of
 * deciding what matters. Here:
 *
 *   - three HERO figures — the volume and the two timings — read first
 *   - three supporting figures carry a proportion bar, because "25 no-reason"
 *     means nothing without "of 55"
 *   - the ranked list below shows distribution, which no single number can
 *
 * Every figure comes from the backend's `summary`, already volume-weighted.
 * Nothing is recomputed from `records`: a second implementation of the weighting
 * would eventually disagree with the table's own footer, and whichever one a
 * reader happened to look at is the one they would quote.
 */

const DASH = '—';

const mins = (value) => (
    value === null || value === undefined || value === '' ? DASH : Number(value).toFixed(1)
);

const Hero = ({ label, value, unit, sub }) => (
    <div className="cgr-hero">
        <div className="cgr-hero__label">{label}</div>
        <div className="cgr-hero__value">
            {value}
            {unit ? <span className="cgr-hero__unit">{unit}</span> : null}
        </div>
        {sub ? <div className="cgr-hero__sub">{sub}</div> : null}
    </div>
);

/**
 * A count, with the proportion drawn beneath it.
 *
 * **The headline count and the bar deliberately face opposite ways.** `value` is
 * the raw count of the thing named — 43 events with no reason — because that is
 * the number someone acts on. The BAR and the line under it state the positive
 * complement, so a fuller bar is always the better result and the three cards can
 * be compared at a glance instead of each being read to work out which direction
 * it runs in.
 *
 * `barValue` is therefore its own prop, not derived from `value`.
 *
 * `denomLabel` is not decoration: two of these are shares of events and one is a
 * share of caregivers. It was hardcoded to "events", which made the roster card
 * read "88.9% of events" about a number that had nothing to do with events.
 */
const Meter = ({ label, value, barValue, total, tone, phrase, denomLabel = 'events' }) => {
    const pctNum = total ? (barValue / total) * 100 : 0;
    return (
        <div className="cgr-meter">
            <div className="cgr-meter__top">
                <span className="cgr-meter__label">{label}</span>
                <span className="cgr-meter__value">{(value || 0).toLocaleString()}</span>
            </div>
            <div className="cgr-meter__track" role="presentation">
                <div
                    className={`cgr-meter__fill cgr-meter__fill--${tone || 'neutral'}`}
                    style={{ width: `${Math.min(100, Math.max(0, pctNum))}%` }}
                />
            </div>
            <div className="cgr-meter__sub">
                {total
                    ? `${pctNum.toFixed(1)}% of ${denomLabel} ${phrase}`
                    : `no ${denomLabel}`}
            </div>
        </div>
    );
};

const CaregiverSummary = ({ summary, records, isCloud }) => {
    const s = summary || {};
    const totalEvents = s.total_events || 0;
    const caregivers = s.caregiver_count || 0;
    const reporting = s.reporting_caregivers || 0;
    const silent = Math.max(0, caregivers - reporting);

    /* The complements. Clamped at zero because these are two independent figures
       from the backend, and a count exceeding the total would otherwise render a
       negative and a bar running backwards. */
    const withReason = Math.max(0, totalEvents - (s.no_reason_events || 0));
    const acceptedFirst = Math.max(0, totalEvents - (s.cleared_before_accepted || 0));

    const top = [...(records || [])]
        .filter((r) => r.total_events > 0)
        .sort((a, b) => b.total_events - a.total_events)
        .slice(0, 8);
    const peak = top.length ? top[0].total_events : 1;

    return (
        <div className="cgr-summary">
            <div className="cgr-heroes">
                <Hero
                    label="Total Events"
                    value={totalEvents.toLocaleString()}
                    sub={caregivers
                        ? `${reporting} of ${caregivers} caregivers responded${silent ? ` · ${silent} had none` : ''}`
                        : 'no caregivers on this campus'}
                />
                <Hero
                    label="Avg Response"
                    value={mins(s.avg_response_time)}
                    unit="min"
                    sub="event start to device cleared, weighted by events"
                />
                <Hero
                    label="Avg Service"
                    value={mins(s.avg_service_time)}
                    unit="min"
                    /* The denominator is named because it is NOT the event count:
                       events with no measurable service period are excluded, and on
                       many campuses that is most of them. */
                    sub={`device cleared to finished, over ${(s.service_time_events || 0).toLocaleString()} measurable events`}
                />
            </div>

            {/* Headline counts name the exception; the bars and sub-lines state the
                positive complement, so a fuller bar is always better. */}
            <div className="cgr-meters">
                <Meter
                    label="No Reason"
                    value={s.no_reason_events || 0}
                    barValue={withReason}
                    total={totalEvents}
                    tone="good"
                    phrase="finished with a reason code"
                />
                <Meter
                    label="Cleared Before Accept"
                    value={s.cleared_before_accepted || 0}
                    barValue={acceptedFirst}
                    total={totalEvents}
                    tone="good"
                    phrase="accepted and cleared in the proper order"
                />
                <Meter
                    label="Caregivers With No Events"
                    value={silent}
                    barValue={reporting}
                    total={caregivers}
                    tone="good"
                    denomLabel="caregivers"
                    phrase="took events"
                />
            </div>

            {/* The service-time caveat from the plan, stated where the number is read
                rather than only in the doc. On a non-cloud campus an auto-closed event
                records reason 0, which is indistinguishable from "no reason recorded",
                so those events cannot be excluded and the average is inflated. */}
            {!isCloud ? (
                <p className="cgr-caveat">
                    <strong>Service time note:</strong> on this platform an automatically
                    closed alert records the same reason code as one where no reason was
                    entered, so auto-closed events cannot be separated out. Average service
                    time therefore includes them and reads higher than it otherwise would.
                </p>
            ) : null}

            {top.length ? (
                <div className="cgr-rank">
                    <div className="cgr-section-title">Events by caregiver</div>
                    {/* Column labels sit ABOVE the rows they name. Below, a reader
                        has already had to guess what the four numeric columns were. */}
                    <div className="cgr-rank__legend">
                        <span>Caregiver</span>
                        <span className="cgr-rank__legend-spacer" />
                        <span>Events</span>
                        <span>Share</span>
                        <span>Resp</span>
                        <span>Svc</span>
                    </div>
                    <ul className="cgr-rank__list">
                        {top.map((r) => (
                            <li className="cgr-rank__row" key={r.user_id}>
                                <span className="cgr-rank__name" title={r.display_name}>{r.display_name}</span>
                                <span className="cgr-rank__track">
                                    <span
                                        className="cgr-rank__fill"
                                        style={{ width: `${Math.max(2, (r.total_events / peak) * 100)}%` }}
                                        aria-hidden="true"
                                    />
                                </span>
                                <span className="cgr-rank__count">{r.total_events}</span>
                                <span className="cgr-rank__pct">
                                    {totalEvents ? `${((r.total_events / totalEvents) * 100).toFixed(1)}%` : DASH}
                                </span>
                                <span className="cgr-rank__stat" title="Average response, minutes">
                                    {mins(r.avg_response_time)}
                                </span>
                                <span className="cgr-rank__stat" title="Average service, minutes">
                                    {mins(r.avg_service_time)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export default CaregiverSummary;
